// /src/pages/Contact.js
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import seo from '../seo';

export default function Contact() {
  const { t } = useLanguage();
  const { theme } = useTheme();

  const pageTitle = t.contactPage?.title || 'Contact';
  const description =
    t.contactPage?.subtitle || t.footer?.description || 'Contactez-nous.';
  const url = seo.canonical('/contact');

  const card =
    theme === 'dark'
      ? 'bg-gray-800 border border-gray-700 text-gray-100'
      : 'bg-white border border-gray-200 text-gray-900';
  const input =
    theme === 'dark'
      ? 'w-full rounded-lg bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500'
      : 'w-full rounded-lg bg-white border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500';
  const label = theme === 'dark' ? 'text-gray-200' : 'text-gray-800';
  const muted = theme === 'dark' ? 'text-gray-300' : 'text-gray-600';
  const btn =
    theme === 'dark'
      ? 'inline-flex items-center justify-center px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition'
      : 'inline-flex items-center justify-center px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition';

  const [state, setState] = useState({ name: '', email: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null); // null | 'ok' | 'err'

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      // branche ton endpoint si nécessaire
      await new Promise((r) => setTimeout(r, 600));
      setSent('ok');
      setState({ name: '', email: '', message: '' });
    } catch {
      setSent('err');
    } finally {
      setSending(false);
    }
  };

  return (
    // Wrapper pour un fond gris pâle (light) / gris très foncé (dark)
    <div className={`${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'} transition-colors`}>
      <main className="max-w-3xl mx-auto px-4 py-10">
        <Helmet>
          <title>{pageTitle}</title>
          <link rel="canonical" href={url} />
          <meta name="description" content={description} />
          <meta name="robots" content="index,follow" />
          <script type="application/ld+json">
            {JSON.stringify(seo.webPage({ name: pageTitle, url, description }))}
          </script>
          <script type="application/ld+json">
            {JSON.stringify(seo.org())}
          </script>
        </Helmet>

        <div className={`rounded-xl p-6 ${card}`}>
          <h1 className={theme === 'dark' ? 'text-3xl font-bold text-white' : 'text-3xl font-bold text-gray-900'}>
            {t.contactPage?.title}
          </h1>
          <p className={`${muted} mt-3`}>{t.contactPage?.subtitle}</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5">
            <div>
              <label className={`${label} block mb-1`}>{t.contactPage?.nameLabel}</label>
              <input
                className={input}
                placeholder={t.contactPage?.namePlaceholder}
                value={state.name}
                onChange={(e) => setState((s) => ({ ...s, name: e.target.value }))}
                required
              />
            </div>

            <div>
              <label className={`${label} block mb-1`}>{t.contactPage?.emailLabel}</label>
              <input
                type="email"
                className={input}
                placeholder={t.contactPage?.emailPlaceholder}
                value={state.email}
                onChange={(e) => setState((s) => ({ ...s, email: e.target.value }))}
                required
              />
            </div>

            <div>
              <label className={`${label} block mb-1`}>{t.contactPage?.messageLabel}</label>
              <textarea
                className={input}
                rows={5}
                placeholder={t.contactPage?.messagePlaceholder}
                value={state.message}
                onChange={(e) => setState((s) => ({ ...s, message: e.target.value }))}
                required
              />
            </div>

            <button className={btn} disabled={sending} type="submit">
              {sending ? t.contactPage?.sending : t.contactPage?.send}
            </button>

            {sent === 'ok' && (
              <div
                className={
                  theme === 'dark'
                    ? 'mt-4 rounded-lg p-3 bg-green-900 border border-green-700 text-green-100'
                    : 'mt-4 rounded-lg p-3 bg-green-50 border border-green-200 text-green-800'
                }
              >
                <div className="font-medium">{t.contactPage?.successTitle}</div>
                <div className="text-sm mt-1">{t.contactPage?.successBody}</div>
              </div>
            )}

            {sent === 'err' && (
              <div
                className={
                  theme === 'dark'
                    ? 'mt-4 rounded-lg p-3 bg-red-900 border border-red-700 text-red-100'
                    : 'mt-4 rounded-lg p-3 bg-red-50 border border-red-200 text-red-800'
                }
              >
                <div className="font-medium">{t.contactPage?.errorTitle}</div>
                <div className="text-sm mt-1">{t.contactPage?.errorBody}</div>
              </div>
            )}
          </form>
        </div>
      </main>
    </div>
  );
}
